import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import { useSeo } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";
import { categories, siteInfo } from "@/mocks/site";
import type { AnimeWork } from "@/types";

export default function WorkDetail() {
  const { id } = useParams();
  const index = animeList.findIndex((item) => item.id === id);
  const work = (index >= 0 ? animeList[index] : undefined) as AnimeWork | undefined;

  const category = work ? categories.find((cat) => cat.id === work.category) : undefined;
  const prev = index > 0 ? (animeList[index - 1] as AnimeWork) : undefined;
  const next = index >= 0 && index < animeList.length - 1 ? (animeList[index + 1] as AnimeWork) : undefined;
  const related = work
    ? (animeList.filter((item) => item.category === work.category && item.id !== work.id) as AnimeWork[])
    : [];

  useSeo({
    title: work ? `${work.title} - 作品资料与推荐 | 樱花动漫社` : "作品未找到 | 樱花动漫社",
    description: work
      ? `${work.title}（${work.titleJp}）作品介绍：${work.summary} 了解它的制作团队、题材标签与观影提示。`
      : "该作品暂未收录，返回作品库浏览其他内容。",
    keywords: work ? `${work.title},${work.category},${work.tags.join(",")},动漫推荐` : "动漫作品",
    path: work ? `/works/${work.id}` : "/works",
    image: work?.cover,
    jsonLd: work
      ? [
          {
            "@context": "https://schema.org",
            "@type": "TVSeries",
            name: work.title,
            alternateName: work.titleJp,
            url: `${siteInfo.domain}/works/${work.id}`,
            image: work.cover,
            description: work.summary,
            numberOfEpisodes: work.episodes,
            inLanguage: "zh-CN",
            genre: work.tags,
            datePublished: String(work.year),
            productionCompany: { "@type": "Organization", name: work.studio },
            director: { "@type": "Person", name: work.director },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
              { "@type": "ListItem", position: 2, name: "作品库", item: `${siteInfo.domain}/works` },
              { "@type": "ListItem", position: 3, name: work.title, item: `${siteInfo.domain}/works/${work.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!work) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-700 text-foreground-950">这部作品暂未收录</h1>
        <p className="mt-3 text-sm text-foreground-600">可能是链接有误，回到作品库看看其他内容吧。</p>
        <Link
          to="/works"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回作品库
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "作品库", to: "/works" }, { label: work.title }]} />

      {/* 作品头部 */}
      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        <div className="mx-auto w-full max-w-xs lg:mx-0 lg:max-w-none">
          <div className="overflow-hidden rounded-xl border border-background-200 bg-background-100">
            <img
              src={work.cover}
              alt={`《${work.title}》动画封面插画`}
              width={600}
              height={800}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/works?category=${work.category}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-800 transition-colors hover:bg-primary-200"
            >
              <i className={category?.icon || "ri-bookmark-line"} aria-hidden="true"></i>
              {category?.name}
            </Link>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              {work.status}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-3xl font-700 tracking-tight text-foreground-950 md:text-4xl">
            {work.title}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">{work.titleJp}</p>

          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {work.summary}
          </p>

          <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 border-y border-background-200 py-5 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-foreground-500">首播年份</dt>
              <dd className="mt-1 text-foreground-900">{work.year} 年</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">季度</dt>
              <dd className="mt-1 text-foreground-900">{work.season}季</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">集数</dt>
              <dd className="mt-1 text-foreground-900">全 {work.episodes} 集</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">制作公司</dt>
              <dd className="mt-1 text-foreground-900">{work.studio}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">导演</dt>
              <dd className="mt-1 text-foreground-900">{work.director}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">状态</dt>
              <dd className="mt-1 text-foreground-900">{work.status}</dd>
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            {work.tags.map((tag) => (
              <Link
                key={tag}
                to={`/works?tag=${encodeURIComponent(tag)}`}
                className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-background-300"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 正文 + 侧栏 */}
      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        <article className="min-w-0">
          <section>
            <h2 className="flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true"></span>
              剧情简介
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
              {work.synopsis.map((paragraph) => (
                <p key={paragraph.slice(0, 12)}>{paragraph}</p>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
              主要看点
            </h2>
            <ul className="mt-5 space-y-3">
              {work.highlights.map((item) => (
                <li
                  key={item.slice(0, 12)}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 text-sm leading-relaxed text-foreground-700"
                >
                  <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500" aria-hidden="true"></i>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-secondary-500" aria-hidden="true"></span>
              主要角色
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {work.characters.map((role) => (
                <div key={role.name} className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-user-smile-line" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-600 text-foreground-950">{role.name}</h3>
                  <p className="mt-0.5 text-xs text-primary-600">{role.role}</p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{role.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 上一部 / 下一部 */}
          <nav className="mt-12 grid grid-cols-1 gap-4 border-t border-background-200 pt-8 sm:grid-cols-2" aria-label="作品导航">
            {prev ? (
              <Link
                to={`/works/${prev.id}`}
                className="group flex flex-col rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-arrow-left-line" aria-hidden="true"></i> 上一部
                </span>
                <span className="mt-2 font-heading text-sm font-600 text-foreground-950 group-hover:text-primary-700">
                  {prev.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link
                to={`/works/${next.id}`}
                className="group flex flex-col items-end rounded-xl border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300"
              >
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  下一部 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
                <span className="mt-2 font-heading text-sm font-600 text-foreground-950 group-hover:text-primary-700">
                  {next.title}
                </span>
              </Link>
            )}
          </nav>
        </article>

        {/* 侧栏 */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">作品信息</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">分类</dt>
                <dd className="text-foreground-900">{category?.name}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">年份</dt>
                <dd className="text-foreground-900">{work.year}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">集数</dt>
                <dd className="text-foreground-900">{work.episodes}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">制作公司</dt>
                <dd className="text-foreground-900">{work.studio}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">状态</dt>
                <dd className="text-foreground-900">{work.status}</dd>
              </div>
            </dl>
            <Link
              to="/works"
              className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-stack-line" aria-hidden="true"></i>
              返回作品库
            </Link>
          </div>

          {related.length > 0 && (
            <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-600 text-foreground-950">同类作品</h2>
              <ul className="mt-4 space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/works/${item.id}`} className="group flex gap-3">
                      <img
                        src={item.cover}
                        alt={`《${item.title}》封面`}
                        width={80}
                        height={104}
                        className="h-[68px] w-[52px] shrink-0 rounded-md object-cover object-top"
                      />
                      <span className="flex flex-col">
                        <span className="font-heading text-sm font-600 text-foreground-900 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </span>
                        <span className="mt-1 text-xs text-foreground-500">{item.year} · {item.episodes} 集</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">你可能也想看看</h2>
          <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
            {related.slice(0, 4).map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}